/**
 * Search Form Field Components
 *
 * Thin form-binding wrappers that delegate all rendering and data-fetching
 * to domain search components (SearchEvent, SearchProduct, SearchMembership).
 *
 * These components bridge react-hook-form's ControllerRenderProps with
 * the domain component API (value/onChange).
 *
 * Type assertions are used because FormField config types define lighter-weight
 * item types (extending ComboBoxInputItemType), while domain components
 * use fuller serializer types (extending VenueBasedEventSerializer, etc.).
 * At runtime these are compatible since the same objects flow through.
 */
import { ControllerRenderProps, UseFormReturn } from "react-hook-form";
import SearchEvent from "@app-contexts/dashboard/components/SearchEvent/SearchEvent.web";
import type { SearchEventProps } from "@app-contexts/dashboard/components/SearchEvent/SearchEvent.web";
import SearchProduct from "@app-contexts/dashboard/components/SearchProduct/SearchProduct.web";
import type { SearchProductProps } from "@app-contexts/dashboard/components/SearchProduct/SearchProduct.web";
import SearchMembership from "@app-contexts/dashboard/components/SearchMembership/SearchMembership.web";
import type { SearchMembershipProps } from "@app-contexts/dashboard/components/SearchMembership/SearchMembership.web";
import type {
  SearchEventFormFieldConfig,
  SearchProductFormFieldConfig,
  SearchMembershipFormFieldConfig,
} from "./types";

// ============================================================================
// SHARED TYPES (for backwards compatibility - re-export from types)
// ============================================================================

export type { EventSearchItemType as EventSearchItem } from "./types";
export type { ProductSearchItemType as ProductSearchItem } from "./types";
export type { MembershipSearchItemType as MembershipSearchItem } from "./types";

// ============================================================================
// SEARCH EVENT FIELD
// ============================================================================
// FormField renders config.label above the input, so the inner search component
// always receives an empty label to avoid duplication. For full control over
// label placement, use FORM_FIELD_TYPES.CUSTOM with a render function that
// renders SearchEvent/SearchProduct/SearchMembership directly.

export function SearchEventField({
  field,
  config,
  form,
}: {
  field: ControllerRenderProps;
  config: SearchEventFormFieldConfig;
  form: UseFormReturn;
}): JSX.Element {
  const props = {
    service: config.service,
    fetchOptions: config.fetchOptions,
    searchParams: config.searchParams,
    single: config.multiple === false,
    value: config.getFormValue ? undefined : field.value,
    onChange: (value: unknown) => {
      field.onChange(value);
      if (config.onSelectionChange) {
        const items = Array.isArray(value) ? value : [value].filter(Boolean);
        config.onSelectionChange(items, form);
      }
    },
    showSelectedBelow: config.showSelectedBelow,
    withSubItems: config.withSubItems,
    counters: config.counters,
    filterOptions: config.filterOptions,
    placeholder: config.placeholder ?? "Search events",
    id: config.name ?? "search-event",
    label: "",
    getFormValue: config.getFormValue,
    renderSelectedItem: config.renderSelectedItem,
    subItemsKey: config.subItemsKey,
    selectedItemsDisplay: config.selectedItemsDisplay,
  } as unknown as SearchEventProps;

  return <SearchEvent {...props} />;
}

// ============================================================================
// SEARCH PRODUCT FIELD
// ============================================================================

export function SearchProductField({
  field,
  config,
  form,
}: {
  field: ControllerRenderProps;
  config: SearchProductFormFieldConfig;
  form: UseFormReturn;
}): JSX.Element {
  const props = {
    service: config.service,
    fetchOptions: config.fetchOptions,
    searchParams: config.searchParams,
    single: config.multiple === false,
    value: config.getFormValue ? undefined : field.value,
    onChange: (value: unknown) => {
      field.onChange(value);
      if (config.onSelectionChange) {
        const items = Array.isArray(value) ? value : [value].filter(Boolean);
        config.onSelectionChange(items, form);
      }
    },
    showSelectedBelow: config.showSelectedBelow,
    withSubItems: config.withSubItems,
    counters: config.counters,
    filterOptions: config.filterOptions,
    placeholder: config.placeholder ?? "Search products",
    id: config.name ?? "search-product",
    label: "",
    getFormValue: config.getFormValue,
    renderSelectedItem: config.renderSelectedItem,
    subItemsKey: config.subItemsKey,
    selectedItemsDisplay: config.selectedItemsDisplay,
  } as unknown as SearchProductProps;

  return <SearchProduct {...props} />;
}

// ============================================================================
// SEARCH MEMBERSHIP FIELD
// ============================================================================

export function SearchMembershipField({
  field,
  config,
  form,
}: {
  field: ControllerRenderProps;
  config: SearchMembershipFormFieldConfig;
  form: UseFormReturn;
}): JSX.Element {
  const props = {
    service: config.service,
    fetchOptions: config.fetchOptions,
    searchParams: config.searchParams,
    single: config.multiple === false,
    value: config.getFormValue ? undefined : field.value,
    onChange: (value: unknown) => {
      field.onChange(value);
      if (config.onSelectionChange) {
        const items = Array.isArray(value) ? value : [value].filter(Boolean);
        config.onSelectionChange(items, form);
      }
    },
    showSelectedBelow: config.showSelectedBelow,
    withSubItems: config.withSubItems,
    counters: config.counters,
    filterOptions: config.filterOptions,
    placeholder: config.placeholder ?? "Search memberships",
    id: config.name ?? "search-membership",
    label: "",
    getFormValue: config.getFormValue,
    renderSelectedItem: config.renderSelectedItem,
    subItemsKey: config.subItemsKey,
    selectedItemsDisplay: config.selectedItemsDisplay,
  } as unknown as SearchMembershipProps;

  return <SearchMembership {...props} />;
}
